import classNames from 'classnames';
import React from 'react';
import type { GenericUIProps, TransformedCSSProperties } from '../component.types';
import type { UntitledUIIconName } from '../Icon';
import UntitledIcon from '../Icon';
import styles from './Frame.module.scss';

export interface ContainerProps {
  /** Optionally, pass media as `children` */
  children?: React.ReactNode;
  /** Specify shape of frame -- defaults to `square` */
  shape?: 'circle' | 'square';
  /** Set a size of frame -- defaults to `medium± */
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  /** Set media inside a colored frame */
  coloring?: boolean | 'inherit' | 'eucalyptus' | 'neutral';
  /** Whether the media should fill the container */
  stretchContent?: boolean;
  className?: string;
  /** Apply (currentColor) border? Always set by `Text` if no coloring is supplied */
  borders?: boolean;
}

function Container({
  shape = 'square',
  size = 'medium',
  coloring = false,
  stretchContent = true,
  children,
  className,
  borders,
}: ContainerProps) {
  return (
    <div
      className={classNames(
        styles.container,
        styles[`shape-${shape}`],
        styles[`size-${size}`],
        {
          [styles.borders]: borders,
          [styles.coloring]: coloring,
          [styles.stretchContent]: stretchContent,
          [styles[`color-${coloring}`]]: coloring && typeof coloring === 'string',
        },
        className
      )}
    >
      {children}
    </div>
  );
}

export interface ImageProps
  extends TransformedCSSProperties,
    Pick<GenericUIProps, 'as'>,
    React.HTMLAttributes<HTMLImageElement>,
    ContainerProps {
  src?: string;
  alt?: string;
}

function Image(props: ImageProps) {
  const {
    shape,
    size,
    coloring,
    stretchContent,
    as: Component = 'img',
    children,
    borders,
    ...rest
  } = props;
  return (
    <Container
      shape={shape}
      size={size}
      coloring={coloring}
      borders={borders}
      stretchContent={stretchContent}
    >
      {children ? children : <Component alt="" {...rest} />}
    </Container>
  );
}

function Avatar(props: ImageProps) {
  if (props.src) return <Image shape="circle" {...props} />;
  return (
    <Text coloring shape="circle" {...props}>
      {props.alt}
    </Text>
  );
}

export interface IconProps extends ContainerProps {
  /** Pass the UntitledUI icon name; alternatively, pass full icon as child */
  name?: UntitledUIIconName;
}

function Icon(props: IconProps) {
  const { shape, size, coloring, stretchContent, name, borders, children, ...rest } = props;
  return (
    <Container
      shape={shape}
      size={size}
      coloring={coloring}
      borders={borders}
      stretchContent={stretchContent}
    >
      {!!name && !children ? <UntitledIcon name={name} {...rest} /> : children}
    </Container>
  );
}

function Text(props: ContainerProps) {
  const { shape, size, coloring, borders, children } = props;
  return (
    <Container
      shape={shape}
      size={size}
      coloring={coloring}
      borders={!coloring || borders === true}
      className={styles.text}
    >
      {typeof children === 'string' ? children.substring(0, 2) : children}
    </Container>
  );
}

/** Frames an image, icon, textual icon or avatar with a standardized shape/size/border */
export default {
  /** Frame an image by passing an `<img.. />` as child, or setting `src` directly */
  Image,
  /** Frame an Icon, either by passing `Icon` as child, or setting icon `name` directly */
  Icon,
  /** Use text in lue of an image/icon; automatically strips to 2 characters */
  Text,
  /** Handy wrapper around `Image` /w `Text` fallback to the `alt` value */
  Avatar,
};
